import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DELIVERY_PARTNER_IDS_MAP, NO_TRACKING_REQUIRED_PARTNER_IDS } from './constants';
import { createManualDelivery, getUserPatientAddresses, validateManualDeliveryDetails } from './helper';
import DeliveryExecutiveFields from './DeliveryExecutiveFields';
import RecipientSelector from './RecipientSelector';

const initialDeliveryData = {
    orderId: '',
    referenceNumber: '',
    trackingLink: '',
    billingAddressId: '',
    deliveryPartnerId: '',
    deliveryExecutiveName: '',
    deliveryExecutivePhoneNumber: '',
    recipientName: '',
    recipientPhone: '',
    packageDetails: {
        units: 1,
        subTotal: 0,
        isCod: false,
        codAmount: 0,
        containsLiquid: false,
    },
};

const ManualDeliveryForm = ({ orderId, mapUserPatient, address, deliveryPartnerId, onClose, orderData, deliveryBoxImages = [] }) => {
    const [userPatientAddresses, setUserPatientAddresses] = useState([address]);
    const [deliveryData, setDeliveryData] = useState(initialDeliveryData);
    const [loading, setLoading] = useState(false);

    const isSelf = parseInt(deliveryPartnerId) === DELIVERY_PARTNER_IDS_MAP.SELF;
    const noTrackingRequired = NO_TRACKING_REQUIRED_PARTNER_IDS.includes(parseInt(deliveryPartnerId));

    const selectedAddress = useMemo(
        () => userPatientAddresses?.find((a) => String(a.id) === String(deliveryData.billingAddressId)),
        [userPatientAddresses, deliveryData.billingAddressId]
    );

    useEffect(() => {
        (async () => {
            if (mapUserPatient) {
                const res = await getUserPatientAddresses(mapUserPatient.id);
                if (res.length > 0) {
                    setUserPatientAddresses(res);
                }
            }
        })();
    }, [mapUserPatient]);

    useEffect(() => {
        setDeliveryData((prevState) => ({
            ...prevState,
            orderId,
            billingAddressId: address?.id,
            deliveryPartnerId,
            packageDetails: {
                ...prevState.packageDetails,
                subTotal: orderData?.total ?? 0,
                codAmount: prevState.packageDetails.isCod ? (orderData?.total ?? 0) : 0,
            },
        }));
    }, [orderId, address?.id, deliveryPartnerId, orderData?.total]);

    const handleBillingAddressChange = (addressId) => {
        setDeliveryData((prevState) => ({
            ...prevState,
            billingAddressId: addressId,
        }));
    };

    const handleSubmit = async () => {
        const payload = {
            ...deliveryData,
            deliveryPartnerId: parseInt(deliveryPartnerId),
            orderId: parseInt(deliveryData.orderId),
            billingAddressId: parseInt(deliveryData.billingAddressId),
        };

        if (!validateManualDeliveryDetails(payload, noTrackingRequired)) {
            return;
        }

        payload.packageDetails = {
            ...payload.packageDetails,
            codAmount: payload.packageDetails.isCod ? (payload.packageDetails.codAmount || 0) : 0,
        };

        if (noTrackingRequired) {
            delete payload.referenceNumber;
            delete payload.trackingLink;
        } else {
            // Optional -- left blank, they are simply not sent, and can be added later.
            payload.referenceNumber = String(payload.referenceNumber || '').trim();
            payload.trackingLink = String(payload.trackingLink || '').trim();
            if (!payload.referenceNumber) delete payload.referenceNumber;
            if (!payload.trackingLink) delete payload.trackingLink;
        }

        if (isSelf) {
            delete payload.recipientName;
            delete payload.recipientPhone;
        } else {
            delete payload.deliveryExecutiveName;
            delete payload.deliveryExecutivePhoneNumber;
            if (!payload.recipientName) delete payload.recipientName;
            if (!payload.recipientPhone) delete payload.recipientPhone;
        }

        // Open-box photos, from the modal shell. Added at submit time so nothing above
        // this line — validation included — sees a changed payload shape.
        payload.deliveryBoxImages = deliveryBoxImages;

        setLoading(true);
        const result = await createManualDelivery(payload);
        setLoading(false);

        if (result?.success) {
            onClose();
        }
    };

    return (
        <div className='flex flex-col gap-[0.9rem]'>
            {!noTrackingRequired && (
                <div className='flex flex-col'>
                    <label>Reference / AWB number <span className='text-gray-500'>(optional — can be added later)</span></label>
                    <input
                        type='text'
                        className='w-full'
                        value={deliveryData.referenceNumber}
                        onChange={(e) => setDeliveryData((prev) => ({ ...prev, referenceNumber: e.target.value }))}
                        placeholder='AWB or shipment reference'
                    />
                </div>
            )}

            {!noTrackingRequired && (
                <div className='flex flex-col'>
                    <label>Tracking link <span className='text-gray-500'>(optional — can be added later)</span></label>
                    <input
                        type='url'
                        className='w-full'
                        value={deliveryData.trackingLink}
                        onChange={(e) => setDeliveryData((prev) => ({ ...prev, trackingLink: e.target.value }))}
                        placeholder='https://...'
                    />
                </div>
            )}

            <div className='flex flex-col'>
                <label>Select billing address</label>
                <select
                    className='w-full'
                    value={deliveryData.billingAddressId}
                    onChange={(e) => handleBillingAddressChange(e.target.value)}
                >
                    {userPatientAddresses?.map((userPatientAddress) => (
                        <option key={userPatientAddress.id} value={userPatientAddress.id}>
                            {`${userPatientAddress.name} | ${userPatientAddress.phone} | ${userPatientAddress.line}, ${userPatientAddress.district}, ${userPatientAddress.state}, ${userPatientAddress.pincode}`}
                        </option>
                    ))}
                </select>
            </div>

            <div className='flex gap-[0.9rem]'>
                <div className='flex flex-col flex-1'>
                    <label>Units</label>
                    <input
                        type='number'
                        min={1}
                        value={deliveryData.packageDetails.units}
                        onChange={(e) => setDeliveryData((prev) => ({
                            ...prev,
                            packageDetails: { ...prev.packageDetails, units: parseInt(e.target.value) || 1 },
                        }))}
                    />
                </div>
                <div className='flex flex-col flex-1'>
                    <label>Total amount</label>
                    <input
                        type='number'
                        min={0}
                        value={deliveryData.packageDetails.subTotal}
                        onChange={(e) => setDeliveryData((prev) => {
                            const subTotal = parseFloat(e.target.value) || 0;
                            return {
                                ...prev,
                                packageDetails: {
                                    ...prev.packageDetails,
                                    subTotal,
                                    codAmount: prev.packageDetails.isCod ? subTotal : 0,
                                },
                            };
                        })}
                    />
                </div>
            </div>

            <div className='flex flex-col gap-[0.9rem]'>
                <div className='flex gap-[0.5rem] items-center'>
                    <input
                        id='manual-is-cod-input'
                        type='checkbox'
                        className='m-0 p-0'
                        checked={deliveryData.packageDetails.isCod}
                        onChange={() => setDeliveryData((prev) => ({
                            ...prev,
                            packageDetails: {
                                ...prev.packageDetails,
                                isCod: !prev.packageDetails.isCod,
                                codAmount: !prev.packageDetails.isCod ? prev.packageDetails.subTotal : 0,
                            },
                        }))}
                    />
                    <label className='m-0 p-0' htmlFor='manual-is-cod-input'>Is COD</label>
                </div>
                {deliveryData.packageDetails.isCod && (
                    <div className='flex flex-col flex-1'>
                        <label>COD amount</label>
                        <input
                            type='number'
                            min={0}
                            value={deliveryData.packageDetails.codAmount}
                            onChange={(e) => setDeliveryData((prev) => ({
                                ...prev,
                                packageDetails: { ...prev.packageDetails, codAmount: parseInt(e.target.value) || 0 },
                            }))}
                        />
                    </div>
                )}
            </div>

            {!isSelf && (
                <RecipientSelector
                    userName={mapUserPatient?.User?.name || ''}
                    userPhone={mapUserPatient?.User?.contact_no || ''}
                    patientName={mapUserPatient?.Patient?.name || ''}
                    patientPhone={mapUserPatient?.Patient?.contact_no || ''}
                    addressName={selectedAddress?.name || ''}
                    addressPhone={selectedAddress?.phone || ''}
                    recipientName={deliveryData.recipientName || ''}
                    recipientPhone={deliveryData.recipientPhone || ''}
                    onNameChange={(val) => setDeliveryData((prev) => ({ ...prev, recipientName: val }))}
                    onPhoneChange={(val) => setDeliveryData((prev) => ({ ...prev, recipientPhone: val }))}
                />
            )}

            {isSelf && (
                <>
                    <DeliveryExecutiveFields
                        name={deliveryData.deliveryExecutiveName}
                        phone={deliveryData.deliveryExecutivePhoneNumber}
                        onNameChange={(value) => setDeliveryData((prev) => ({ ...prev, deliveryExecutiveName: value }))}
                        onPhoneChange={(value) => setDeliveryData((prev) => ({ ...prev, deliveryExecutivePhoneNumber: value }))}
                    />
                    <p className='text-sm text-gray-600 m-0'>
                        In-house delivery: no tracking link required. Customer will receive invoice via WhatsApp.
                    </p>
                </>
            )}

            <div className='flex justify-end'>
                <button
                    type='button'
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`bg-[#28A48B] p-[1rem] rounded-[0.5rem] text-white-50 font-semibold ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {loading ? 'Creating...' : 'Create manual delivery'}
                </button>
            </div>
        </div>
    );
};

ManualDeliveryForm.propTypes = {
    deliveryBoxImages: PropTypes.arrayOf(PropTypes.string),
    orderId: PropTypes.number.isRequired,
    mapUserPatient: PropTypes.object.isRequired,
    address: PropTypes.object.isRequired,
    deliveryPartnerId: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
    orderData: PropTypes.object,
};

export default ManualDeliveryForm;
